/**
 * Strip a leading "P&ID Graph " / "PFD Graph " prefix from a document name.
 *
 * The pre-FS-469 backend prepended this prefix to graph rows at creation
 * time. New graphs no longer carry the prefix (graph-creation.service.ts),
 * but legacy rows persisted in the DB still do. Apply this helper in any
 * board UI render path so the prefix never reaches the user, regardless of
 * doc type, fallback path, or whether the source PNID lookup resolved.
 *
 * Idempotent — running it twice is a no-op.
 */
export function stripGraphPrefix(name: string): string {
  if (!name) return name
  return name
    .replace(/^P&ID Graph\s+/i, '')
    .replace(/^PFD Graph\s+/i, '')
}

/**
 * Strip a leading "P&ID " / "PFD " prefix from a document name *only when*
 * the document's `type` is `pnid` / `pfd`. The prefix on a P&ID-typed row
 * is redundant — the type pill already conveys it — and is the
 * legacy-data marker users see on opened files. Type-gated so a legitimate
 * filename like `P&ID Drawing 5.pdf` on a `type='document'` row stays
 * untouched.
 *
 * Idempotent — running it twice is a no-op.
 */
export function stripTypePrefix(name: string, docType: string | undefined): string {
  if (!name) return name
  if (docType === 'pnid') return name.replace(/^P&ID\s+/i, '')
  if (docType === 'pfd') return name.replace(/^PFD\s+/i, '')
  return name
}

/**
 * Compose both strips for any user-visible document-name render path.
 * Pass the *effective* doc type — for graph cards that resolve to a source
 * P&ID/PFD record, that's the source's type, not `'graph'`.
 */
export function cleanDocumentName(name: string, docType?: string): string {
  return stripTypePrefix(stripGraphPrefix(name), docType)
}
